export const ROLE_META = {
  admin: { label: "Admin", roleClass: "admin", pillClass: "red" },
  state_officer: { label: "State Officer", roleClass: "state", pillClass: "" },
  district_officer: { label: "District Officer", roleClass: "district", pillClass: "purple" },
  viewer: { label: "Viewer", roleClass: "viewer", pillClass: "green" },
};

export const ROLE_DEFINITIONS = [
  {
    id: "admin",
    iconClass: "red",
    title: "Full Platform Control",
    description: "Can view, export, manage users and import data.",
    permissions: [
      { label: "View", muted: false },
      { label: "Export", muted: false },
      { label: "Users", muted: false },
      { label: "Import", muted: false },
    ],
  },
  {
    id: "state_officer",
    iconClass: "",
    title: "State Monitoring Role",
    description: "Can view and export statewide reports; cannot manage users or import data.",
    permissions: [
      { label: "View", muted: false },
      { label: "Export", muted: false },
      { label: "Users", muted: true },
      { label: "Import", muted: true },
    ],
  },
  {
    id: "district_officer",
    iconClass: "purple",
    title: "District-Only Access",
    description: "Can view and export district data only; no user or import rights.",
    permissions: [
      { label: "District View", muted: false },
      { label: "Export", muted: false },
      { label: "Users", muted: true },
      { label: "Import", muted: true },
    ],
  },
  {
    id: "viewer",
    iconClass: "green",
    title: "Read-Only Reviewer",
    description: "Can view dashboards only; export, user management and data import are disabled.",
    permissions: [
      { label: "View", muted: false },
      { label: "Export", muted: true },
      { label: "Users", muted: true },
      { label: "Import", muted: true },
    ],
  },
];

export const PERMISSION_MATRIX = [
  {
    role: "admin",
    view: { text: "✓ Allowed", className: "check" },
    export: { text: "✓ Allowed", className: "check" },
    users: { text: "✓ Allowed", className: "check" },
    import: { text: "✓ Allowed", className: "check" },
    risk: { label: "High Privilege", className: "orange" },
    control: "Quarterly access review",
  },
  {
    role: "state_officer",
    view: { text: "✓ Allowed", className: "check" },
    export: { text: "✓ Allowed", className: "check" },
    users: { text: "× Blocked", className: "cross" },
    import: { text: "× Blocked", className: "cross" },
    risk: { label: "Medium", className: "" },
    control: "Export monitoring",
  },
  {
    role: "district_officer",
    view: { text: "District only", className: "limited" },
    export: { text: "✓ Allowed", className: "check" },
    users: { text: "× Blocked", className: "cross" },
    import: { text: "× Blocked", className: "cross" },
    risk: { label: "Medium", className: "" },
    control: "Scope validation",
  },
  {
    role: "viewer",
    view: { text: "✓ Allowed", className: "check" },
    export: { text: "× Blocked", className: "cross" },
    users: { text: "× Blocked", className: "cross" },
    import: { text: "× Blocked", className: "cross" },
    risk: { label: "Low", className: "green" },
    control: "Read-only review",
  },
];

export const QUICK_ACTIONS = [
  { id: "add", target: "um-users", iconClass: "", title: "Add or Invite User", description: "Create a user and assign role, district and school scope in one flow." },
  { id: "permissions", target: "um-permissions", iconClass: "green", title: "Review Permissions", description: "Check view, export, import and user-management rights by role." },
  { id: "scope", target: "um-users", iconClass: "orange", title: "Assign Scope", description: "Map officers to state, district, block or school responsibility." },
  { id: "audit", target: "um-audit", iconClass: "purple", title: "Export Audit Log", description: "Download role and permission changes for governance review." },
];

const countByRole = (users = [], role) => users.filter((u) => u.role === role && u.is_active).length;

export const buildPermissionMatrix = (users = []) =>
  PERMISSION_MATRIX.map((row) => {
    const activeCount = countByRole(users, row.role);
    const risk =
      row.role === "admin" && activeCount <= 1
        ? { label: "High Privilege · Single Admin", className: "orange" }
        : activeCount === 0
          ? { label: "Unassigned Role", className: "orange" }
          : row.risk;
    return {
      ...row,
      risk,
      control: `${row.control} · ${activeCount} active user${activeCount === 1 ? "" : "s"}`,
    };
  });

export const buildAccessHealth = (users = []) => {
  const active = users.filter((u) => u.is_active);
  const inactive = users.length - active.length;
  const rolesUsed = new Set(active.map((u) => u.role)).size;
  const adminCount = countByRole(users, "admin");

  let score = 100;
  if (inactive > 0) score -= inactive * 8;
  if (rolesUsed < 4) score -= (4 - rolesUsed) * 6;
  if (adminCount <= 1) score -= 4;
  score = Math.min(99, Math.max(40, Math.round(score)));

  const headline =
    adminCount <= 1 && rolesUsed < 3
      ? "Access is clean, but role review is due"
      : inactive > 0
      ? "Inactive accounts need review"
      : "Access governance is in good shape";

  const narrative =
    adminCount <= 1
      ? "Only one active administrator is present. Add role owners, assign fallback approvers and review import permissions before the next reporting cycle."
      : "User roles and scopes are configured. Continue periodic reviews to keep access resilient.";

  const chips = [];
  if (inactive === 0) chips.push({ label: "No Inactive Users", className: "green" });
  else chips.push({ label: `${inactive} Inactive Users`, className: "amber" });
  chips.push({ label: `${rolesUsed} of 4 Roles in Use`, className: rolesUsed < 4 ? "amber" : "" });
  if (rolesUsed < 4) chips.push({ label: "Quarterly Review Pending", className: "amber" });

  const primaryAction =
    adminCount <= 1
      ? "Create State Officer and District Officer accounts so data import and user administration are not dependent on one administrator."
      : "Review role assignments quarterly and ensure import permissions stay aligned with officer responsibilities.";

  return {
    score,
    headline,
    narrative,
    chips,
    primaryAction,
    ringStyle: {
      background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--um-success) 0 ${score}%, rgba(16,185,129,.14) ${score}% 100%)`,
    },
  };
};

export const buildHeroKpis = (users = []) => {
  const active = users.filter((u) => u.is_active);
  const inactive = users.length - active.length;
  const admins = users.filter((u) => u.role === "admin" && u.is_active);
  const rolesUsed = new Set(active.map((u) => u.role)).size;
  const emptyRoles = 4 - rolesUsed;
  const coverage = Math.round((rolesUsed / 4) * 100);

  return [
    {
      label: "Total Users",
      value: String(users.length),
      sub: `${active.length} active account${active.length === 1 ? "" : "s"}`,
      subClass: "",
    },
    {
      label: "Admin Users",
      value: String(admins.length),
      sub: "Full access",
      subClass: "neutral",
    },
    {
      label: "Inactive Accounts",
      value: String(inactive),
      sub: inactive === 0 ? "All accounts active" : "Needs review",
      subClass: inactive > 0 ? "warn" : "",
    },
    {
      label: "Role Coverage",
      value: `${coverage}%`,
      sub: emptyRoles > 0 ? `${emptyRoles} roles empty` : "All roles assigned",
      subClass: emptyRoles > 0 ? "warn" : "",
    },
  ];
};

export const buildRoleCards = (users = []) =>
  ROLE_DEFINITIONS.map((role) => ({
    ...role,
    count: countByRole(users, role.id),
    meta: ROLE_META[role.id],
  }));

const countModifiedUsers = (users = []) =>
  users.filter((user) => {
    if (!user.created_at || !user.updated_at) return false;
    const created = new Date(user.created_at).getTime();
    const updated = new Date(user.updated_at).getTime();
    return Number.isFinite(created) && Number.isFinite(updated) && updated - created > 60_000;
  }).length;

export const buildGovernanceChecks = (users = []) => {
  const active = users.filter((u) => u.is_active);
  const inactive = users.length - active.length;
  const rolesUsed = new Set(active.map((u) => u.role)).size;
  const hasReviewer = countByRole(users, "state_officer") > 0 || countByRole(users, "district_officer") > 0;
  const withTimestamps = users.filter((u) => u.created_at).length;
  const modifiedUsers = countModifiedUsers(users);

  return [
    {
      title: "Active User Verified",
      description: inactive === 0 ? "No inactive or locked users in current scope." : `${inactive} inactive user(s) in scope.`,
      iconClass: "green",
      status: inactive === 0 ? "Pass" : "Review",
      pillClass: inactive === 0 ? "green" : "orange",
    },
    {
      title: "Role Coverage Gap",
      description: rolesUsed < 4 ? "State, district and viewer roles have no users." : "All configured roles have at least one user.",
      iconClass: "orange",
      status: rolesUsed < 4 ? "Review" : "Pass",
      pillClass: rolesUsed < 4 ? "orange" : "green",
    },
    {
      title: "Audit Trail Enabled",
      description:
        withTimestamps === users.length
          ? modifiedUsers > 0
            ? `${modifiedUsers} account change${modifiedUsers === 1 ? "" : "s"} recorded with timestamps.`
            : "All accounts have audit timestamps ready for export."
          : `${users.length - withTimestamps} account(s) missing created timestamps.`,
      iconClass: "",
      status: withTimestamps === users.length ? "Pass" : "Review",
      pillClass: withTimestamps === users.length ? "green" : "orange",
    },
    {
      title: "Maker-Checker Pending",
      description: hasReviewer ? "Reviewer role assigned for bulk import oversight." : "Add one reviewer before enabling bulk import.",
      iconClass: "purple",
      status: hasReviewer ? "Pass" : "Due",
      pillClass: hasReviewer ? "green" : "orange",
    },
  ];
};

export const buildAccessMaturity = (users = []) => {
  const checks = buildGovernanceChecks(users);
  const passed = checks.filter((check) => check.status === "Pass").length;
  const pct = checks.length ? Math.round((passed / checks.length) * 100) : 0;
  const rolesUsed = new Set(users.filter((u) => u.is_active).map((u) => u.role)).size;
  return {
    pct,
    description:
      pct >= 75
        ? `${passed} of ${checks.length} governance checks passing. Maintain quarterly access reviews.`
        : rolesUsed < 3
        ? "Good foundational setup. Improve resilience by distributing admin duties across officers."
        : `${passed} of ${checks.length} governance checks passing. Close remaining access gaps.`,
  };
};

export const getUserScope = (user) => {
  if (user.role === "district_officer" && user.district_code) return user.district_code;
  if (user.role === "admin" || user.role === "state_officer") return "State-wide";
  return user.district_code || "Dashboard scope";
};

export const getUserInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const getUserSubtitle = (user) => {
  if (user.role === "admin") return "Primary platform owner";
  const meta = ROLE_META[user.role];
  return meta ? `${meta.label} account` : "Platform user";
};

export const filterUsers = (users = [], { search = "", role = "all", status = "active" } = {}) => {
  const q = search.trim().toLowerCase();
  return users.filter((user) => {
    if (role !== "all" && user.role !== role) return false;
    if (status === "active" && !user.is_active) return false;
    if (status === "inactive" && user.is_active) return false;
    if (!q) return true;
    const meta = ROLE_META[user.role];
    const haystack = [user.full_name, user.email, user.role, meta?.label, user.district_code]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
};

export const buildInsightCards = (users = []) => {
  const active = users.filter((u) => u.is_active);
  const inactive = users.length - active.length;
  const rolesUsed = new Set(active.map((u) => u.role)).size;
  const emptyRoles = ROLE_DEFINITIONS.filter((role) => countByRole(users, role.id) === 0);
  const modifiedUsers = countModifiedUsers(users);
  const coverageRows = emptyRoles.slice(0, 3).map((role) => ({
    text: `Assign ${ROLE_META[role.id]?.label || role.id} (0 active users)`,
    className: role.id === "admin" ? "orange" : "",
  }));

  return [
    {
      iconClass: "",
      title: "Access Coverage Insight",
      description:
        active.length <= 1
          ? `The system has ${active.length} active account${active.length === 1 ? "" : "s"}. ${emptyRoles.length} role${emptyRoles.length === 1 ? "" : "s"} still unassigned.`
          : `${active.length} active accounts across ${rolesUsed} of ${ROLE_DEFINITIONS.length} roles.`,
      rows:
        coverageRows.length > 0
          ? coverageRows
          : [{ text: "All configured roles have at least one active user", className: "green" }],
    },
    {
      iconClass: "green",
      title: "Current Strength",
      description: `${countByRole(users, "admin")} admin account(s), ${rolesUsed} roles in use, ${inactive} inactive account(s).`,
      rows: [
        {
          text: inactive === 0 ? "No inactive users" : `Review ${inactive} inactive account${inactive === 1 ? "" : "s"}`,
          className: inactive === 0 ? "green" : "orange",
        },
        {
          text: `${ROLE_DEFINITIONS.length} role definitions configured for ${users.length} total users`,
          className: "green",
        },
      ],
    },
    {
      iconClass: "purple",
      title: "Audit Readiness",
      description:
        modifiedUsers > 0
          ? `${modifiedUsers} profile or permission change${modifiedUsers === 1 ? "" : "s"} recorded in user timestamps.`
          : "No profile changes recorded yet beyond initial account creation.",
      rows: [
        { text: `Export matrix for ${users.length} user${users.length === 1 ? "" : "s"}`, className: "" },
        {
          text:
            inactive > 0 || emptyRoles.length > 0
              ? `Resolve ${inactive} inactive and ${emptyRoles.length} empty role gaps`
              : "All roles staffed and accounts active",
          className: inactive > 0 || emptyRoles.length > 0 ? "orange" : "green",
        },
      ],
    },
  ];
};

export const buildUserManagementMarkdown = (users = []) => {
  const cards = buildInsightCards(users);
  const governance = buildGovernanceChecks(users);
  const maturity = buildAccessMaturity(users);
  return [
    "## Insights",
    ...cards.map((c) => `- ${c.title}: ${c.description}`),
    "",
    "## Recommendations",
    ...governance.map((g) => `- ${g.title}: ${g.description}`),
    "",
    "## Priority Action Items",
    `- ${maturity.description}`,
    `- Review inactive accounts and empty roles before the next audit cycle.`,
  ].join("\n");
};
